import {Controller, useFormContext} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n'
import {GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Switch} from '@/components/commons/switch/Switch'
import {InputText} from '@/components/commons/input-text/InputText'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CareNetworkFieldsSchema} from '../../formSchemas'
import {CARE_NETWORK_SWITCHES, careNetworkMapToLabel} from '../../utils'
import {SwitchField} from '../switch-field/SwitchField'

type SwitchField = keyof typeof CARE_NETWORK_SWITCHES

const RELATED_FIELDS_MAP = {
    volunteerNetwork: {
        label: 'volunteerNetworkDetails',
        name: 'volunteerNetworkDetails'
    },
    paidNetwork: {
        label: 'paidNetworkDetails',
        name: 'paidNetworkDetails'
    },
    activatedTerritorialServices: {
        label: 'activatedTerritorialServices',
        name: 'activatedTerritorialServices'
    },
    homeCare: {
        label: 'homeCareNotes',
        name: 'homeCareNotes'
    },
    transport: {
        label: 'transportNotes',
        name: 'transportNotes'
    },
    dayCenters: {
        label: 'dayCentersNotes',
        name: 'dayCentersNotes'
    },
    associations: {
        label: 'associationsNotes',
        name: 'associationsNotes'
    },
    otherServices: {
        label: 'otherServicesNotes',
        name: 'otherServicesNotes'
    }
} as const satisfies Record<
    SwitchField,
    {
        label: keyof typeof careNetworkMapToLabel
        name: keyof CareNetworkFieldsSchema
    }
>

export const CareNetworkSwitchField = ({field}: {field: SwitchField}) => {
    const {t} = useTranslation()
    const {getValues, setValue} = useFormContext<CareNetworkFieldsSchema>()

    const handleSwitchChange = (checked: boolean) => {
        setValue(field, checked, {shouldDirty: true})
        // Clear the related fields when switch is turned off
        if (!checked) {
            if (field == 'activatedTerritorialServices') {
                setValue('municipalTerritorialServices', '', {shouldDirty: true})
                setValue('aslTerritorialServices', '', {shouldDirty: true})
            } else {
                const {name} = RELATED_FIELDS_MAP[field]
                setValue(name, '', {shouldDirty: true})
            }
        }
    }

    const renderRelatedField = () => {
        if (!getValues(field)) return null

        // Special case for activatedTerritorialServices
        if (field == 'activatedTerritorialServices') {
            return (
                <Flexbox gap={2} fullWidth>
                    <InputText
                        type="text"
                        label={t(careNetworkMapToLabel.municipalTerritorialServices)}
                        name="municipalTerritorialServices"
                        placeholder={t('medical_records:insert_info')}
                    />
                    <InputText
                        type="text"
                        label={t(careNetworkMapToLabel.aslTerritorialServices)}
                        name="aslTerritorialServices"
                        placeholder={t('medical_records:insert_info')}
                    />
                </Flexbox>
            )
        }

        // Default case for other fields
        const {label, name} = RELATED_FIELDS_MAP[field]
        return (
            <InputText
                type="text"
                label={t(careNetworkMapToLabel[label])}
                name={name}
                placeholder={t('medical_records:insert_info')}
            />
        )
    }

    return (
        <SwitchField columns={3} height="58px" columnGap={4} fullWidth>
            <Controller
                name={field}
                render={({field: fieldProps}) => (
                    <>
                        <GridItem spanColumn={1}>
                            <Switch
                                size="sm"
                                label={t(careNetworkMapToLabel[field])}
                                defaultChecked={fieldProps.value}
                                onChange={handleSwitchChange}
                            />
                        </GridItem>
                        <GridItem spanColumn={2}>{renderRelatedField()}</GridItem>
                    </>
                )}
            />
        </SwitchField>
    )
}
